import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import WorksheetField from './components/WorksheetField';
import {
  worksheetFieldsGridStyles,
  worksheetFullWidthFieldStyles,
  worksheetSectionStyles,
} from './WorksheetFieldsLayout.styles';
import { WorksheetFieldsLayoutTestIds } from './WorksheetFieldsLayout.testIds';
import {
  buildWorksheetSections,
  isWorksheetFieldFullWidth,
  isWorksheetFieldVisible,
} from './WorksheetFieldsLayout.utils';

/**
 * The worksheet's questions in sections, two to a row where they fit, with follow-up questions
 * shown only when the answer they depend on calls for them. Sections, follow-ups and formats come
 * from each field's description (see parseWorksheetFieldSettings) — editable in Document Types. A
 * computed field is read-only: the worksheet keeps it up to date.
 */
const WorksheetFieldsLayout = ({
  fields,
  form,
  errors,
  disabled = false,
  fieldHints = {},
  setInForm,
  validateField,
  onChange,
  onAdd = null,
  onRemove = null,
}) => {
  const sections = useMemo(() => buildWorksheetSections(fields), [fields]);
  const fieldsByName = useMemo(() => _.keyBy(fields, 'name'), [fields]);

  return (
    <Box data-testid={WorksheetFieldsLayoutTestIds.LAYOUT}>
      {sections.map(({ key, title, items }) => (
        <Box
          data-testid={WorksheetFieldsLayoutTestIds.SECTION}
          key={key}
          sx={worksheetSectionStyles}
        >
          {!!title && (
            <Typography
              className="mb-12"
              component="h3"
              data-testid={WorksheetFieldsLayoutTestIds.SECTION_TITLE}
              variant="bold16"
            >
              {title}
            </Typography>
          )}

          <Box sx={worksheetFieldsGridStyles}>
            {items
              .filter(({ settings }) =>
                isWorksheetFieldVisible({ showIf: settings.showIf, fieldsByName, form }),
              )
              .map(({ field, settings }) => (
                <Box
                  data-testid={WorksheetFieldsLayoutTestIds.FIELD}
                  key={field.id}
                  sx={
                    isWorksheetFieldFullWidth({ field, settings })
                      ? worksheetFullWidthFieldStyles
                      : null
                  }
                >
                  <WorksheetField
                    disabled={disabled || !!settings.compute}
                    errors={errors}
                    extraHint={fieldHints[field.id] ?? null}
                    field={field}
                    form={form}
                    setInForm={setInForm}
                    settings={settings}
                    validateField={validateField}
                    onAdd={onAdd}
                    onChange={onChange}
                    onRemove={onRemove}
                  />
                </Box>
              ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

WorksheetFieldsLayout.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  fieldHints: PropTypes.objectOf(PropTypes.string),
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
};

export default memo(WorksheetFieldsLayout);
